// apps/accounts/src/pages/VendorPayments.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatDate } from '../utils/dateHelpers';
import pb from '../lib/pocketbaseClient';
import { cn } from '../lib/utils';

export default function VendorPayments() {
  const [payments, setPayments] = useState([]);
  const [bills, setBills] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bills');
  const [search, setSearch] = useState('');
  const [filterVendor, setFilterVendor] = useState('All');
  const [filterProject, setFilterProject] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('bill');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    vendor: '',
    project: '',
    billNumber: '',
    billDate: '',
    billAmount: '',
    description: '',
    paymentAmount: '',
    paymentDate: '',
    paymentMethod: 'cash',
    transactionId: '',
    note: '',
    paidFrom: 'office_fund',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const isMounted = useRef(true);

  const paymentMethods = ['cash', 'bank_transfer', 'cheque', 'upi'];

  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => { isMounted.current = false; };
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, billsRes, projectsRes, vendorsRes, partnersRes] = await Promise.all([
        pb.collection('vendor_payments').getFullList({ sort: '-paymentDate', expand: 'project,vendor,paidFrom' }),
        pb.collection('vendor_bills').getFullList({ sort: '-billDate', expand: 'project,vendor' }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
        pb.collection('vendors').getFullList({ sort: 'name' }),
        pb.collection('partners').getFullList({ sort: 'name' }),
      ]);
      if (isMounted.current) {
        setPayments(paymentsRes);
        setBills(billsRes);
        setProjects(projectsRes);
        setVendors(vendorsRes);
        setPartners(partnersRes);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      let data = {};
      if (modalType === 'bill') {
        data = {
          vendor: formData.vendor,
          project: formData.project || null,
          billNumber: formData.billNumber || null,
          billDate: formData.billDate,
          billAmount: parseFloat(formData.billAmount) || 0,
          description: formData.description || null,
        };
        if (editingId) {
          await pb.collection('vendor_bills').update(editingId, data);
        } else {
          await pb.collection('vendor_bills').create(data);
        }
      } else {
        data = {
          vendor: formData.vendor,
          project: formData.project || null,
          amount: parseFloat(formData.paymentAmount) || 0,
          paymentDate: formData.paymentDate,
          paymentMethod: formData.paymentMethod,
          transactionId: formData.transactionId || null,
          note: formData.note || null,
          paidFrom: formData.paidFrom || 'office_fund',
        };
        if (editingId) {
          await pb.collection('vendor_payments').update(editingId, data);
        } else {
          await pb.collection('vendor_payments').create(data);
        }
      }
      handleCloseModal();
      fetchData();
    } catch (err) {
      let msg = err.message || 'Failed to save';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join('; ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  const handleEdit = (item, type) => {
    setModalType(type);
    setEditingId(item.id);
    if (type === 'bill') {
      setFormData({
        vendor: item.vendor || '',
        project: item.project || '',
        billNumber: item.billNumber || '',
        billDate: item.billDate || '',
        billAmount: item.billAmount || '',
        description: item.description || '',
        paymentAmount: '',
        paymentDate: '',
        paymentMethod: 'cash',
        transactionId: '',
        note: '',
        paidFrom: 'office_fund',
      });
    } else {
      setFormData({
        vendor: item.vendor || '',
        project: item.project || '',
        billNumber: '',
        billDate: '',
        billAmount: '',
        description: '',
        paymentAmount: item.amount || '',
        paymentDate: item.paymentDate || '',
        paymentMethod: item.paymentMethod || 'cash',
        transactionId: item.transactionId || '',
        note: item.note || '',
        paidFrom: item.paidFrom || 'office_fund',
      });
    }
    setShowModal(true);
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const collection = type === 'bill' ? 'vendor_bills' : 'vendor_payments';
      await pb.collection(collection).delete(id);
      fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      vendor: '',
      project: '',
      billNumber: '',
      billDate: '',
      billAmount: '',
      description: '',
      paymentAmount: '',
      paymentDate: '',
      paymentMethod: 'cash',
      transactionId: '',
      note: '',
      paidFrom: 'office_fund',
    });
    setFormError('');
    setFormLoading(false);
  };

  const openBillModal = () => {
    setModalType('bill');
    setEditingId(null);
    setFormData({
      vendor: '',
      project: '',
      billNumber: '',
      billDate: '',
      billAmount: '',
      description: '',
      paymentAmount: '',
      paymentDate: '',
      paymentMethod: 'cash',
      transactionId: '',
      note: '',
      paidFrom: 'office_fund',
    });
    setShowModal(true);
  };

  const openPaymentModal = (vendorId = '') => {
    setModalType('payment');
    setEditingId(null);
    setFormData({
      vendor: vendorId || '',
      project: '',
      billNumber: '',
      billDate: '',
      billAmount: '',
      description: '',
      paymentAmount: '',
      paymentDate: '',
      paymentMethod: 'cash',
      transactionId: '',
      note: '',
      paidFrom: 'office_fund',
    });
    setShowModal(true);
  };

  const getVendorSummary = () => {
    const summary = {};
    vendors.forEach(v => {
      const vendorBills = bills.filter(b => b.vendor === v.id);
      const vendorPayments = payments.filter(p => p.vendor === v.id);
      const totalBills = vendorBills.reduce((sum, b) => sum + (b.billAmount || 0), 0);
      const totalPaid = vendorPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const totalDue = totalBills - totalPaid;
      const lastBillDate = vendorBills.length > 0 
        ? vendorBills.sort((a, b) => new Date(b.billDate) - new Date(a.billDate))[0]?.billDate 
        : null;
      summary[v.id] = { totalBills, totalPaid, totalDue, lastBillDate, billCount: vendorBills.length, paymentCount: vendorPayments.length };
    });
    return summary;
  };

  const vendorSummary = getVendorSummary();

  const filteredBills = bills.filter(b => {
    const matchVendor = filterVendor === 'All' || b.vendor === filterVendor;
    const matchProject = filterProject === 'All' || b.project === filterProject;
    const matchSearch = b.description?.toLowerCase().includes(search.toLowerCase()) ||
                        b.billNumber?.toLowerCase().includes(search.toLowerCase()) ||
                        b.expand?.vendor?.name?.toLowerCase().includes(search.toLowerCase());
    const matchDate = (!dateFrom || b.billDate >= dateFrom) && (!dateTo || b.billDate <= dateTo);
    return matchVendor && matchProject && matchSearch && matchDate;
  });

  const filteredPayments = payments.filter(p => {
    const matchVendor = filterVendor === 'All' || p.vendor === filterVendor;
    const matchProject = filterProject === 'All' || p.project === filterProject;
    const matchSearch = p.note?.toLowerCase().includes(search.toLowerCase()) ||
                        p.transactionId?.toLowerCase().includes(search.toLowerCase()) ||
                        p.expand?.vendor?.name?.toLowerCase().includes(search.toLowerCase());
    const matchDate = (!dateFrom || p.paymentDate >= dateFrom) && (!dateTo || p.paymentDate <= dateTo);
    return matchVendor && matchProject && matchSearch && matchDate;
  });

  const totalBillAmount = filteredBills.reduce((sum, b) => sum + (b.billAmount || 0), 0);
  const totalPaymentAmount = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">📋 Vendor Bills & Payments</h1>
          <p className="text-sm text-slate-500">Manage vendor bills, track payments, and monitor dues</p>
        </div>
        <div className="flex gap-2">
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={openBillModal}>
            + New Bill
          </Button>
          <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => openPaymentModal()}>
            + New Payment
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Total Bills</p><p className="text-2xl font-bold text-slate-800">₹{totalBillAmount.toLocaleString()}</p><p className="text-xs text-slate-400">{filteredBills.length} bills</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Total Payments</p><p className="text-2xl font-bold text-green-600">₹{totalPaymentAmount.toLocaleString()}</p><p className="text-xs text-slate-400">{filteredPayments.length} payments</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Total Due</p><p className="text-2xl font-bold text-red-600">₹{(totalBillAmount - totalPaymentAmount).toLocaleString()}</p><p className="text-xs text-slate-400">Pending amount</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Vendors</p><p className="text-2xl font-bold text-slate-800">{vendors.length}</p><p className="text-xs text-slate-400">Active vendors</p></CardContent></Card>
      </div>

      {/* Vendor-wise Summary */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold text-slate-700 mb-3">📊 Vendor-wise Summary</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr><th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Vendor</th><th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Total Bills</th><th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Total Paid</th><th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Due</th><th className="text-center px-3 py-2 text-xs font-semibold text-slate-600">Bills</th><th className="text-center px-3 py-2 text-xs font-semibold text-slate-600">Payments</th><th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Last Bill</th><th className="text-center px-3 py-2 text-xs font-semibold text-slate-600">Action</th></tr>
              </thead>
              <tbody className="divide-y">
                {vendors.map(v => {
                  const summary = vendorSummary[v.id] || { totalBills: 0, totalPaid: 0, totalDue: 0, lastBillDate: null, billCount: 0, paymentCount: 0 };
                  return (
                    <tr key={v.id} className="hover:bg-slate-50/50">
                      <td className="px-3 py-2 font-medium">{v.name}</td>
                      <td className="px-3 py-2 text-right font-medium">₹{summary.totalBills.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-green-600">₹{summary.totalPaid.toLocaleString()}</td>
                      <td className={cn("px-3 py-2 text-right font-bold", summary.totalDue > 0 ? 'text-red-600' : 'text-green-600')}>₹{summary.totalDue.toLocaleString()}</td>
                      <td className="px-3 py-2 text-center">{summary.billCount}</td>
                      <td className="px-3 py-2 text-center">{summary.paymentCount}</td>
                      <td className="px-3 py-2 text-xs">{summary.lastBillDate ? formatDate(summary.lastBillDate) : '-'}</td>
                      <td className="px-3 py-2 text-center"><Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700" onClick={() => openPaymentModal(v.id)}>Pay</Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="bills">Bills ({bills.length})</TabsTrigger>
          <TabsTrigger value="payments">Payments ({payments.length})</TabsTrigger>
        </TabsList>

        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex-1 min-w-[150px]"><Input placeholder="🔍 Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full" /></div>
          <select value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white"><option value="All">All Vendors</option>{vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
          <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white"><option value="All">All Projects</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
          <div className="flex items-center gap-2"><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" /><span className="text-slate-400">to</span><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" /></div>
          <Button variant="outline" size="sm" onClick={() => { setSearch(''); setFilterVendor('All'); setFilterProject('All'); setDateFrom(''); setDateTo(''); }}>Clear</Button>
        </div>

        {/* Bills Tab */}
        <TabsContent value="bills">
          <Card><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b"><tr><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Bill #</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Date</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Vendor</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Project</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Description</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Amount</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th></tr></thead>
              <tbody className="divide-y">
                {filteredBills.length === 0 ? <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No bills found.</td></tr> :
                filteredBills.map(b => (
                  <tr key={b.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-mono text-xs">{b.billNumber || '-'}</td>
                    <td className="px-4 py-3">{formatDate(b.billDate)}</td>
                    <td className="px-4 py-3 font-medium">{b.expand?.vendor?.name || 'N/A'}</td>
                    <td className="px-4 py-3">{b.expand?.project?.name || 'N/A'}</td>
                    <td className="px-4 py-3 text-xs max-w-xs truncate">{b.description || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">₹{b.billAmount?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-center space-x-1"><Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700" onClick={() => handleEdit(b, 'bill')}>Edit</Button><Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(b.id, 'bill')}>Del</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b"><tr><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Date</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Vendor</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Project</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Method</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Transaction</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Amount</th><th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th></tr></thead>
              <tbody className="divide-y">
                {filteredPayments.length === 0 ? <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No payments found.</td></tr> :
                filteredPayments.map(p => (
                  <tr key={p.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3">{formatDate(p.paymentDate)}</td>
                    <td className="px-4 py-3 font-medium">{p.expand?.vendor?.name || 'N/A'}</td>
                    <td className="px-4 py-3">{p.expand?.project?.name || 'N/A'}</td>
                    <td className="px-4 py-3"><Badge className="bg-indigo-50 text-indigo-700">{p.paymentMethod?.replace('_', ' ')}</Badge></td>
                    <td className="px-4 py-3 text-xs">{p.transactionId || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-green-600">₹{p.amount?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-center space-x-1"><Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700" onClick={() => handleEdit(p, 'payment')}>Edit</Button><Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(p.id, 'payment')}>Del</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-4">{modalType === 'bill' ? (editingId ? '✏️ Edit Bill' : '📄 New Bill') : (editingId ? '✏️ Edit Payment' : '💳 New Payment')}</h2>
              {formError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">{formError}</div>}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Vendor *</label><select name="vendor" value={formData.vendor} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white" required><option value="">Select</option>{vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Project</label><select name="project" value={formData.project} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white"><option value="">Select</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                </div>

                {modalType === 'bill' ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div><label className="block text-sm font-medium text-slate-700 mb-1">Bill Number</label><Input name="billNumber" value={formData.billNumber} onChange={handleChange} /></div>
                      <div><label className="block text-sm font-medium text-slate-700 mb-1">Bill Date *</label><Input name="billDate" type="date" value={formData.billDate} onChange={handleChange} required /></div>
                      <div><label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹) *</label><Input name="billAmount" type="number" value={formData.billAmount} onChange={handleChange} required step="0.01" /></div>
                    </div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label><Input name="description" value={formData.description} onChange={handleChange} placeholder="Work details..." /></div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-slate-700 mb-1">Payment Date *</label><Input name="paymentDate" type="date" value={formData.paymentDate} onChange={handleChange} required /></div>
                      <div><label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹) *</label><Input name="paymentAmount" type="number" value={formData.paymentAmount} onChange={handleChange} required step="0.01" /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-slate-700 mb-1">Payment Method</label><select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white">{paymentMethods.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}</select></div>
                      <div><label className="block text-sm font-medium text-slate-700 mb-1">Transaction ID</label><Input name="transactionId" value={formData.transactionId} onChange={handleChange} /></div>
                    </div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Note</label><Input name="note" value={formData.note} onChange={handleChange} placeholder="Payment reference..." /></div>
                  </>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>{formLoading ? 'Saving...' : (editingId ? 'Update' : 'Create')}</Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}